import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {css} from '@emotion/core'
import {createMarkup, convertDatoCmsImgPathsToLocal} from '../graphql/datoCmsDataFactory'
import {getTheme, getThemeStyles, getThemedImage} from '../util/theme'
import {getLanguageCode} from '../util/locale'

import Layout from '../components/Layout'

import howStartpageWorksData from '../translations/how-startpage-works.json'

const HowStartpageWorks = ({languageCode}) => {
    const lang = languageCode || getLanguageCode()

    const {
        howSeo,
        howStep,
        howSection1Description,
        howSection1Subtitle,
        howSection1Title,
        howSection1Images,
        howSection2Description,
        howSection2Title,
        howStartpageDifference,
    } = howStartpageWorksData[lang]

    const [theme, setTheme] = useState('light')
    const [themeStyles, setThemeStyles] = useState({})

    useEffect(() => {
        setTheme(getTheme())
        setThemeStyles(getThemeStyles())
    }, [theme, themeStyles])

    const howSection1Image = getThemedImage(howSection1Images, theme)

    return (
        <Layout
            title={howSeo.title}
            description={howSeo.description}
            lang={lang}
            canonicalPath="/how-startpage-works/"
            theme={theme}
            themeStyles={themeStyles}
        >
            <div
                css={css`
                    color: ${themeStyles.primaryTextColor};
                    background-color: ${themeStyles.primaryBackgroundColor};
                    margin: 5.4rem 0 0;
                    font-weight: 600;
                    font-size: 18px;
                    line-height: 24px;
                    padding-top: 50px;

                    @media (min-width: 900px) {
                        padding-top: 60px;
                        font-size: 20px;
                    }
                `}
                className="how-sp-works"
            >
                <section
                    className="how-sp-works-section-1"
                    css={css`
                        padding-bottom: 64px;
                        padding-left: 16px;
                        padding-right: 16px;

                        @media (min-width: 900px) {
                            padding-left: 40px;
                            padding-right: 40px;
                            font-size: 20px;
                        }
                    `}
                >
                    <div
                        css={css`
                            max-width: 1200px;
                            margin: 0 auto;
                        `}
                    >
                        <div
                            css={css`
                                color: inherit;
                                @media (min-width: 900px) {
                                    display: flex;
                                    justify-content: space-between;
                                }
                            `}
                            className="how-sp-works__heading"
                        >
                            <div
                                className="how-sp-works__heading-text"
                                css={css`
                                    color: ${themeStyles.primaryTextColor};
                                    @media (max-width: 900px) {
                                        text-align: center;
                                    }
                                `}
                            >
                                {howSection1Subtitle && (
                                    <h6
                                        css={css`
                                            color: ${themeStyles.emphasisTextColor};
                                            margin-bottom: 24px;
                                            font-size: 18px;
                                            line-height: 24px;

                                            @media (min-width: 900px) {
                                                font-size: 20px;
                                                line-height: 24px;
                                            }
                                        `}
                                    >
                                        {howSection1Subtitle}
                                    </h6>
                                )}
                                {howSection1Title && (
                                    <h1
                                        css={css`
                                            color: inherit;
                                            font-weight: 600;
                                            font-size: 40px;
                                            line-height: 46px;
                                            letter-spacing: 0.33px;

                                            @media (min-width: 900px) {
                                                font-size: 48px;
                                                line-height: 48px;
                                            }
                                        `}
                                    >
                                        {howSection1Title}
                                    </h1>
                                )}
                                {howSection1Description && (
                                    <p
                                        css={css`
                                            color: inherit;
                                            font-size: 16px;
                                            line-height: 24px;
                                            font-weight: normal;
                                            margin-top: 24px;
                                            margin-bottom: 32px;

                                            @media (min-width: 900px) {
                                                line-height: 16px;
                                            }
                                        `}
                                    >
                                        {howSection1Description}
                                    </p>
                                )}
                            </div>
                            {howSection1Image && (
                                <div>
                                    <img
                                        css={css`
                                            @media (max-width: 900px) {
                                                display: none;
                                            }
                                        `}
                                        className="how-sp-works__heading-image"
                                        src={convertDatoCmsImgPathsToLocal(howSection1Image.url)}
                                        alt={howSection1Image.alt}
                                    />
                                </div>
                            )}
                        </div>
                        <div
                            className="how-sp-works__differences"
                            css={css`
                                margin-top: 46px;

                                @media (min-width: 1100px) {
                                    padding-top: 2rem;
                                    margin-top: 30px;
                                    display: flex;
                                    border: 1px solid ${themeStyles.chartBorderColor};
                                    border-radius: 4px;
                                    box-shadow: 0px 10px 20px rgba(32, 41, 69, 0.1);

                                    & > div {
                                        flex: 1;
                                    }
                                }
                            `}
                        >
                            {howStartpageDifference &&
                                howStartpageDifference.map((section, i) => {
                                    const image = getThemedImage(section.icon, theme)
                                    return (
                                        <div
                                            key={section.title}
                                            className="how-sp-works__difference"
                                            css={css`
                                                border-radius: 4px;
                                                box-shadow: 0px 10px 20px rgba(32, 41, 69, 0.1);
                                                padding: 26px 8px;
                                                position: relative;
                                                border-radius: 4px;
                                                border: 1px solid ${themeStyles.chartBorderColor};

                                                &:last-of-type {
                                                    margin-top: 46px;
                                                }

                                                @media (min-width: 1100px) {
                                                    border: none;
                                                    box-shadow: none;
                                                    padding: 10px 8px;
                                                    &:first-of-type {
                                                        padding-right: 0;
                                                    }
                                                    &:last-of-type {
                                                        margin-top: 0;
                                                        padding-left: 0;
                                                    }
                                                }
                                            `}
                                        >
                                            <h3
                                                css={css`
                                                    color: ${themeStyles.emphasisTextColor};
                                                    font-weight: 600;
                                                    font-size: 32px;
                                                    line-height: 32px;
                                                    padding-left: 8px;
                                                    position: absolute;
                                                    top: -22px;
                                                `}
                                            >
                                                {section.title}
                                            </h3>
                                            <div
                                                className="how-sp-works__difference-list"
                                                css={css`
                                                    height: 95%;
                                                    ul {
                                                        list-style: none;
                                                        margin: 0;
                                                        padding: 0;
                                                        margin-top: ${i === 1 ? `40px` : 0};


                                                        @media (min-width: 900px) {
                                                            margin-top: 40px;

                                                            height: 100%;
                                                            display: flex;
                                                            flex-direction: column;
                                                        }

                                                        @media (min-width: 941px) {
                                                            margin-top: 14px;
                                                        }

                                                        li {
                                                            color: ${themeStyles.primaryTextColor};
                                                            font-size: 16px;
                                                            line-height: 24px;
                                                            font-weight: normal;
                                                            padding: 10px 8px;
                                                            display: flex;
                                                            align-items: center;
                                                            min-height: 70px;

                                                            @media (min-width: 900px) {
                                                                flex: 1;
                                                            }

                                                            &:nth-of-type(odd) {
                                                                background-color: ${themeStyles.oddListItem};
                                                            }

                                                            &:before {
                                                                content: url('${convertDatoCmsImgPathsToLocal(
                                                                    image.url,
                                                                )}');
                                                                padding-right: 12px;
                                                            }
                                                        }
                                                    }
                                                `}
                                                dangerouslySetInnerHTML={createMarkup(section.facts)}
                                            />
                                        </div>
                                    )
                                })}
                        </div>
                    </div>
                </section>
                <section
                    className="how-sp-works-section-2"
                    css={css`
                        background-color: ${themeStyles.darkerBackgroundColor2};
                        padding-left: 16px;
                        padding-right: 16px;

                        @media (min-width: 900px) {
                            padding-left: 40px;
                            padding-right: 40px;
                            font-size: 20px;
                        }
                    `}
                >
                    <div
                        css={css`
                            max-width: 1200px;
                            margin: 0 auto;
                            color: ${themeStyles.primaryTextColor};
                            padding: 48px 0;

                            @media (min-width: 900px) {
                                padding: 64px 0;
                            }

                            @media (min-width: 1200px) {
                                padding: 80px 0;
                            }
                        `}
                    >
                        <div
                            className="how-sp-works-section-2__heading"
                            css={css`
                                color: inherit;
                                margin-left: 16px;
                                @media (min-width: 900px) {
                                    width: 50%;
                                }
                            `}
                        >
                            {howSection2Title && (
                                <h2
                                    css={css`
                                        color: inherit;
                                        font-weight: 600;
                                        font-size: 40px;
                                        line-height: 46px;
                                        letter-spacing: 0.33px;

                                        @media (min-width: 900px) {
                                            font-size: 48px;
                                            line-height: 48px;
                                        }
                                    `}
                                >
                                    {howSection2Title}
                                </h2>
                            )}
                            {howSection2Description && (
                                <p
                                    css={css`
                                        color: inherit;
                                        font-size: 16px;
                                        line-height: 24px;
                                        font-weight: normal;
                                        margin-top: 24px;

                                        @media (min-width: 900px) {
                                            max-width: 75%;
                                        }
                                    `}
                                >
                                    {howSection2Description}
                                </p>
                            )}
                        </div>
                        {howStep && (
                            <div className="how-sp-works__steps">
                                {howStep.map((step) => {
                                    const image = getThemedImage(step.stepImage, theme)
                                    return (
                                        <div
                                            key={step.stepTitle}
                                            className="how-sp-works__step"
                                            css={css`
                                                @media (min-width: 900px) {
                                                    display: flex;
                                                    gap: 24px;
                                                    align-items: flex-start;

                                                    div {
                                                        width: 50%;
                                                    }

                                                    &:first-of-type {
                                                        margin-top: -5%;
                                                        @media (min-width: 1200px) {
                                                            margin-top: -15%;
                                                        }
                                                        div {
                                                            align-self: flex-end;
                                                        }
                                                    }

                                                    &:nth-of-type(2),
                                                    &:nth-of-type(3),
                                                    &:nth-of-type(5) {
                                                        flex-direction: row-reverse;
                                                    }

                                                    &:nth-of-type(3) {
                                                        div:last-of-type {
                                                            margin-top: -13%;
                                                        }
                                                    }
                                                }
                                            `}
                                        >
                                            <div
                                                className="how-sp-works__step-text"
                                                css={css`
                                                    color: ${themeStyles.primaryTextColor};
                                                    padding-left: 8px;
                                                    border: 1px solid ${themeStyles.chartBorderColor};
                                                    border-radius: 4px;
                                                    box-shadow: 0px 10px 20px rgba(32, 41, 69, 0.1);
                                                    padding: 16px;
                                                    margin-top: 56px;

                                                    h4 {
                                                        color: inherit;
                                                        font-weight: 600;
                                                        font-size: 20px;
                                                        line-height: 24px;
                                                        margin: 16px 0;
                                                    }

                                                    a {
                                                        color: ${themeStyles.emphasisTextColor};
                                                        text-decoration: none;
                                                    }
                                                `}
                                            >
                                                <h3
                                                    css={css`
                                                        color: ${themeStyles.emphasisTextColor};
                                                        font-weight: 600;
                                                        font-size: 32px;
                                                        line-height: 32px;
                                                        margin-top: -32px;
                                                    `}
                                                >
                                                    {step.stepTitle}
                                                </h3>
                                                <h4>{step.stepSubtitle}</h4>
                                                <aside
                                                    css={css`
                                                        p {
                                                            color: ${themeStyles.primaryTextColor};
                                                            font-style: normal;
                                                            font-weight: normal;
                                                            font-size: 16px;
                                                        }
                                                    `}
                                                    dangerouslySetInnerHTML={createMarkup(step.stepDescription)}
                                                />
                                            </div>
                                            <div
                                                className="how-sp-works__step-image"
                                                css={css`
                                                    width: 85%;
                                                    margin: 0 auto;
                                                    margin-top: 40px;
                                                    text-align: center;
                                                `}
                                            >
                                                {image && (
                                                    <img
                                                        css={css`
                                                            width: 100%;
                                                            max-width: 450px;
                                                            @media (min-width: 900px) {
                                                                width: 70%;
                                                            }
                                                        `}
                                                        src={convertDatoCmsImgPathsToLocal(image.url)}
                                                        alt={image.alt}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </Layout>
    )
}

HowStartpageWorks.propTypes = {
    languageCode: PropTypes.string,
}

export default HowStartpageWorks
